import Logger from "@/utility/logger";

const INSERT_IMAGE_TOKEN = "JENLY_INSERT_IMAGE";
const INSERT_IMAGE_EMPTY_TODO = "TODO: Please insert required image into this section.";
const INSERT_IMAGE_DOCUMENT_NOT_FOUND_TODO = "TODO: Document Not Found.";

export interface InsertImageDocumentLookupParams {
  projectId: string;
  filename?: string;
  state?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
}

export interface DownloadDocumentImageParams {
  documentId: string;
  page: number;
  size: string;
  dpi: number;
  format: string;
  color: string;
}

export interface DocumentImageManifestItem {
  page: number;
  size: string;
  dpi: number;
  format: string;
  color: string;
  width_px?: number;
  height_px?: number;
}

interface ResolveInsertImageItemsOptions {
  projectId?: string;
  imageAssets?: any[];
  retrieveDocumentsForImageInsertion: (params: InsertImageDocumentLookupParams) => Promise<any>;
  retrieveDocumentImages: (documentId: string) => Promise<any>;
  downloadDocumentImage: (params: DownloadDocumentImageParams) => Promise<string>;
}

const normalizeTextValue = (value: any, fallback = ""): string => {
  if (value === null || typeof value === "undefined") {
    return fallback;
  }

  const text = String(value).trim();
  return text || fallback;
};

const normalizeRenditionSize = (value: any): string => normalizeTextValue(value, "a4").toLowerCase();

const normalizeRenditionFormat = (value: any): string => {
  const format = normalizeTextValue(value, "png").toLowerCase();
  return format === "jpg" ? "jpeg" : format;
};

const normalizeRenditionColor = (value: any): string => {
  const color = normalizeTextValue(value, "color").toLowerCase();

  if (["black & white", "black and white", "b&w", "bw", "greyscale", "gray", "grey"].includes(color)) {
    return "grayscale";
  }

  return color;
};

const normalizeRenditionDpi = (value: any): number => {
  const dpi = Number(value);
  return Number.isFinite(dpi) && dpi > 0 ? dpi : 150;
};

const normalizeBooleanValue = (value: any): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }

  return false;
};

/**
 * Parses the JSON payload after JENLY_INSERT_IMAGE into an instruction array.
 * Accepts an array, a single object, { items: [...] }, or { images: [...] }.
 * Also recovers from the broken AI shape { [ ... ] } by extracting the inner array.
 * Example: { "fileName": "logo.png" } becomes [{ fileName: "logo.png" }].
 */
const parseInsertImagePayload = (jsonText: string): any[] => {
  let payload;

  try {
    payload = JSON.parse(jsonText);
  } catch (error) {
    // The AI occasionally emits a JSON array wrapped in a bare object with no key —
    // e.g.  { [ {...}, {...} ] }  — which is invalid JSON and makes JSON.parse throw.
    // As a recovery step, we check if the text matches exactly that broken shape.
    // If it does, we extract just the inner [...] array (capture group 1) and re-parse
    // that on its own, which is valid. If the shape doesn't match, it's a different
    // kind of malformed JSON we can't recover from, so we re-throw the original error.
    // e.g. { [{ "documentId": "abc123", "preferredPage": 1 }] } => [{ "documentId": "abc123", "preferredPage": 1 }]
    const wrappedArrayMatch = jsonText.match(/^\s*\{\s*(\[[\s\S]*\])\s*\}\s*$/);

    if (!wrappedArrayMatch) {
      throw error;
    }

    payload = JSON.parse(wrappedArrayMatch[1]);
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.items)) {
    return payload.items;
  }

  if (payload && Array.isArray(payload.images)) {
    return payload.images;
  }

  if (payload && typeof payload === "object") {
    return [payload];
  }

  return [];
};

/**
 * Finds the balanced JSON object or array that appears after JENLY_INSERT_IMAGE.
 * Handles nested objects/arrays and ignores brackets inside quoted strings.
 * Also skips a trailing markdown code fence when the token appears inside ``` blocks.
 * Example: returns the full [{ "fileName": "logo.png" }] block after the token.
 */
const findInsertImageJsonBlock = (markdown: string, tokenIndex: number) => {
  const searchStart = tokenIndex + INSERT_IMAGE_TOKEN.length;
  const jsonStart = (() => {
    for (let i = searchStart; i < markdown.length; i++) {
      if (markdown[i] === "[" || markdown[i] === "{") {
        return i;
      }
    }

    return -1;
  })();

  if (jsonStart === -1) {
    return null;
  }

  const stack: string[] = [];
  let inString = false;
  let isEscaped = false;

  for (let i = jsonStart; i < markdown.length; i++) {
    const char = markdown[i];

    if (inString) {
      if (isEscaped) {
        isEscaped = false;
      } else if (char === "\\") {
        isEscaped = true;
      } else if (char === "\"") {
        inString = false;
      }

      continue;
    }

    if (char === "\"") {
      inString = true;
      continue;
    }

    if (char === "[" || char === "{") {
      stack.push(char === "[" ? "]" : "}");
      continue;
    }

    if (char === "]" || char === "}") {
      if (stack.length === 0 || stack[stack.length - 1] !== char) {
        return null;
      }

      stack.pop();

      if (stack.length === 0) {
        const jsonText = markdown.slice(jsonStart, i + 1);
        const trailingFenceMatch = markdown.slice(i + 1).match(/^\s*```/);

        return {
          jsonText,
          endIndex: i + 1 + (trailingFenceMatch ? trailingFenceMatch[0].length : 0),
        };
      }
    }
  }

  return null;
};

/**
 * Splits generated markdown into normal markdown segments and image instruction segments.
 * Normal markdown stays as { type: "markdown" } for the existing markdown/table parser.
 * JENLY_INSERT_IMAGE payloads become { type: "image", content: instructions }.
 * Example: "Intro\nJENLY_INSERT_IMAGE [...]" becomes markdown + image segments.
 */
export const splitInsertImageMarkdownSegments = (markdown: string) => {
  const segments: any[] = [];
  let cursor = 0;

  while (cursor < markdown.length) {
    const tokenIndex = markdown.indexOf(INSERT_IMAGE_TOKEN, cursor);

    if (tokenIndex === -1) {
      segments.push({ type: "markdown", content: markdown.slice(cursor) });
      break;
    }

    const jsonBlock = findInsertImageJsonBlock(markdown, tokenIndex);

    if (!jsonBlock) {
      segments.push({ type: "markdown", content: markdown.slice(cursor) });
      break;
    }

    const before = markdown.slice(cursor, tokenIndex).replace(/```[a-zA-Z0-9_-]*\s*$/, "");

    if (before.length > 0) {
      segments.push({ type: "markdown", content: before });
    }

    try {
      const instructions = parseInsertImagePayload(jsonBlock.jsonText);

      if (instructions.length === 0) {
        Logger.warn("insertImageInstructions.ts: JENLY_INSERT_IMAGE payload is empty; inserting TODO.");
        segments.push({ type: "markdown", content: INSERT_IMAGE_EMPTY_TODO });
        cursor = jsonBlock.endIndex;
        continue;
      }

      Logger.debug("insertImageInstructions.ts: Parsed JENLY_INSERT_IMAGE instructions:", instructions.length);
      segments.push({
        type: "image",
        content: instructions,
      });
    } catch (error) {
      Logger.warn("insertImageInstructions.ts: Failed to parse JENLY_INSERT_IMAGE payload as JSON:", error);
      segments.push({ type: "markdown", content: markdown.slice(cursor) });
      break;
    }

    cursor = jsonBlock.endIndex;
  }

  return segments;
};

const getInsertImageDocumentId = (instruction: any): string | null => {
  return (
    instruction?.documentID ||
    instruction?.documentId ||
    instruction?.document_id ||
    instruction?.fileID ||
    instruction?.fileId ||
    instruction?.file_id ||
    null
  );
};

const getInsertImageDocumentName = (instruction: any): string | null => {
  return (
    instruction?.documentName ||
    instruction?.document ||
    instruction?.filename ||
    instruction?.fileName ||
    instruction?.name ||
    null
  );
};

const getInsertImageAltText = (instruction: any): string => {
  return normalizeTextValue(
    instruction?.alt_text || instruction?.altText,
    "Image could not be inserted."
  );
};

const getInsertImageCaptionText = (instruction: any): string | null => {
  const captionText =
    instruction?.captionText ||
    instruction?.caption_text ||
    instruction?.captionDescription ||
    instruction?.caption_description ||
    instruction?.title;

  return normalizeTextValue(captionText) || null;
};

const getInsertImageStaticReference = (instruction: any): string | null => {
  const staticReference =
    instruction?.staticReference ||
    instruction?.static_reference ||
    instruction?.referenceText ||
    instruction?.reference_text ||
    instruction?.reference;

  return normalizeTextValue(staticReference) || null;
};

const getInsertImageLayout = (instruction: any) => {
  const layout = instruction?.preferredDestinationLocation;
  const defaultLayout = { position: "center", preserveAspectRatio: true, pageBreakBetweenImages: false };

  if (layout && typeof layout === "object") {
    return { ...defaultLayout, ...layout };
  }

  return defaultLayout;
};

/**
 * Converts preferredPage from the AI instruction into a concrete list of page numbers.
 * Supports one page, an array of pages, object forms like { all: true }, and "all".
 * For "all", it prefers page numbers from the image manifest, then falls back to page_count.
 * Example: preferredPage: [1, 3] returns [1, 3].
 */
const getRequestedInsertImagePages = (
  preferredPage: any,
  manifestItems: DocumentImageManifestItem[],
  pageCount?: number | null
): number[] => {
  if (Array.isArray(preferredPage)) {
    return preferredPage.map((page) => Number(page)).filter((page) => Number.isInteger(page) && page > 0);
  }

  if (preferredPage && typeof preferredPage === "object") {
    if (preferredPage.all === true || normalizeTextValue(preferredPage.type).toLowerCase() === "all") {
      return getRequestedInsertImagePages("all", manifestItems, pageCount);
    }

    const objectPage = Number(preferredPage.page || preferredPage.number || preferredPage.value);
    return Number.isInteger(objectPage) && objectPage > 0 ? [objectPage] : [];
  }

  const pageText = normalizeTextValue(preferredPage, "all").toLowerCase();

  if (pageText !== "all") {
    const page = Number(preferredPage);
    return Number.isInteger(page) && page > 0 ? [page] : [];
  }

  const pagesFromManifest = Array.from(new Set(manifestItems.map((item) => Number(item.page)))).filter(
    (page) => Number.isInteger(page) && page > 0
  );

  if (pagesFromManifest.length > 0) {
    return pagesFromManifest.sort((a, b) => a - b);
  }

  if (typeof pageCount === "number" && pageCount > 0) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  return [];
};

/**
 * Chooses the best available image rendition for one requested page.
 * Applies preferences in priority order: size, color mode, DPI, then format.
 * Each preference narrows the candidates only when at least one candidate matches it.
 * Stops early when one candidate remains; if multiple remain, returns the first one.
 */
const selectBestImageRendition = (manifestItems: DocumentImageManifestItem[], page: number, instruction: any) => {
  const preferredSize = normalizeRenditionSize(instruction?.preferredSize || instruction?.size);
  const preferredFormat = normalizeRenditionFormat(instruction?.preferredFormat || instruction?.format);
  const preferredColor = normalizeRenditionColor(instruction?.preferredColor || instruction?.color);
  const preferredDpi = normalizeRenditionDpi(instruction?.preferredDpi || instruction?.dpi);
  const candidates = manifestItems.filter((item) => Number(item.page) === page);

  if (candidates.length === 0) {
    return null;
  }

  const preferenceFilters: Array<(item: DocumentImageManifestItem) => boolean> = [
    (item) => normalizeRenditionSize(item.size) === preferredSize,
    (item) => normalizeRenditionColor(item.color) === preferredColor,
    (item) => normalizeRenditionDpi(item.dpi) === preferredDpi,
    (item) => normalizeRenditionFormat(item.format) === preferredFormat,
  ];

  let matchingCandidates = candidates;

  for (const matchesPreference of preferenceFilters) {
    if (matchingCandidates.length <= 1) {
      break;
    }

    const filteredCandidates = matchingCandidates.filter(matchesPreference);

    if (filteredCandidates.length > 0) {
      matchingCandidates = filteredCandidates;
    }
  }

  return matchingCandidates[0];
};

/**
 * Resolves an image instruction to a document id.
 * Uses explicit document/file id fields first when available.
 * If only a filename/name is provided, searches project documents for a completed match.
 * Example: fileName: "logo.png" resolves to the matching uploaded document id.
 */
const resolveDocumentIdForInsertImageInstruction = async (
  instruction: any,
  options: ResolveInsertImageItemsOptions
): Promise<{ documentId: string | null; documentNotFound: boolean }> => {
  const documentId = getInsertImageDocumentId(instruction);

  if (documentId) {
    Logger.debug("insertImageInstructions.ts: Using provided document id for image insertion:", documentId);
    return { documentId, documentNotFound: false };
  }

  const documentName = getInsertImageDocumentName(instruction);

  if (!documentName || !options.projectId) {
    Logger.warn("insertImageInstructions.ts: Missing document name or project id for image insertion.", documentName);
    return { documentId: null, documentNotFound: false };
  }

  Logger.debug("insertImageInstructions.ts: Searching document for image insertion:", documentName);
  const documentsResponse = await options.retrieveDocumentsForImageInsertion({
    projectId: options.projectId,
    filename: documentName,
    state: "COMPLETED",
    page: 1,
    perPage: 100,
  });
  const documents = documentsResponse?.items || [];
  const normalizedDocumentName = documentName.toLowerCase();
  const exactMatch = documents.find((document) => document.filename?.toLowerCase() === normalizedDocumentName);
  const matchedDocument = exactMatch || documents[0];

  Logger.debug("insertImageInstructions.ts: Document search completed for image insertion:", {
    documentName,
    count: documents.length,
    matchedDocumentId: matchedDocument?.id || null,
  });

  if (!matchedDocument?.id) {
    Logger.warn("insertImageInstructions.ts: Document not found for image insertion:", documentName);
    return { documentId: null, documentNotFound: true };
  }

  return { documentId: matchedDocument.id, documentNotFound: false };
};

/**
 * Converts unresolved image instruction items into final Word insertion items.
 * Passes through normal text/table/image items, including image items with string content.
 * For instruction arrays, resolves the document, manifest pages, rendition, and base64 image.
 * Example: { type: "image", content: [{ fileName: "logo.png" }] } becomes a base64 image item.
 */
export const resolveInsertImageItems = async (
  items: any[],
  options: ResolveInsertImageItemsOptions
): Promise<any[]> => {
  const resolvedItems: any[] = [];
  let imageBlockIndex = 0;

  for (const item of items) {
    if (item.type !== "image" || !Array.isArray(item.content)) {
      resolvedItems.push(item);
      continue;
    }

    const instructions: any[] = item.content;
    const blockIndex = imageBlockIndex++;

    for (const [itemIndex, instruction] of Array.from(instructions.entries())) {
      const altText = getInsertImageAltText(instruction);

      try {
        const saved = (options.imageAssets || []).filter((asset) =>
          asset.block_index === blockIndex && asset.item_index === itemIndex && asset.data_base64
        );
        if (saved.length) {
          const layout = getInsertImageLayout(instruction);
          for (const [pageIndex, asset] of Array.from(saved.entries())) {
            resolvedItems.push({
              type: "image", content: asset.data_base64, alt: altText,
              caption: normalizeBooleanValue(instruction?.caption),
              captionText: getInsertImageCaptionText(instruction),
              staticReference: getInsertImageStaticReference(instruction),
              page: asset.page, width_px: asset.width_px, height_px: asset.height_px,
              preserveAspectRatio: layout.preserveAspectRatio, position: layout.position,
              pageBreakAfter: layout.pageBreakBetweenImages && pageIndex < saved.length - 1,
              sourceAsset: asset,
            });
          }
          continue;
        }
        const { documentId, documentNotFound } = await resolveDocumentIdForInsertImageInstruction(instruction, options);

        if (documentNotFound) {
          resolvedItems.push({ type: "text", content: INSERT_IMAGE_DOCUMENT_NOT_FOUND_TODO });
          continue;
        }

        if (!documentId) {
          resolvedItems.push({ type: "text", content: altText });
          continue;
        }

        Logger.debug("insertImageInstructions.ts: Retrieving document images for insertion:", documentId);
        const manifest = await options.retrieveDocumentImages(documentId);
        const manifestItems = manifest?.items || [];

        if (manifestItems.length === 0) {
          Logger.warn("insertImageInstructions.ts: No images found for document image insertion:", documentId);
          resolvedItems.push({ type: "text", content: INSERT_IMAGE_EMPTY_TODO });
          continue;
        }

        const requestedPages = getRequestedInsertImagePages(
          instruction?.preferredPage,
          manifestItems,
          manifest?.page_count
        );

        if (requestedPages.length === 0) {
          resolvedItems.push({
            type: "text",
            content: manifest?.image_extraction_error || altText,
          });
          continue;
        }

        const layout = getInsertImageLayout(instruction);
        const { pageBreakBetweenImages, preserveAspectRatio, position } = layout;
        const caption = normalizeBooleanValue(instruction?.caption);
        const captionText = getInsertImageCaptionText(instruction);
        const staticReference = getInsertImageStaticReference(instruction);

        for (let pageIndex = 0; pageIndex < requestedPages.length; pageIndex++) {
          const page = requestedPages[pageIndex];
          const rendition = selectBestImageRendition(manifestItems, page, instruction);

          if (!rendition) {
            Logger.warn("insertImageInstructions.ts: Image rendition not found for insertion:", documentId, page);
            resolvedItems.push({ type: "text", content: altText });
            continue;
          }

          Logger.debug("insertImageInstructions.ts: Downloading document image for insertion:", documentId, page);
          const base64Image = await options.downloadDocumentImage({
            documentId,
            page,
            size: rendition.size,
            dpi: rendition.dpi,
            format: rendition.format,
            color: rendition.color,
          });

          resolvedItems.push({
            type: "image",
            content: base64Image,
            sourceAsset: {
              block_index: blockIndex, item_index: itemIndex, document_id: documentId, page,
              data_base64: base64Image, width_px: rendition.width_px, height_px: rendition.height_px,
            },
            alt: altText,
            caption,
            captionText,
            staticReference,
            page,
            width_px: rendition.width_px,
            height_px: rendition.height_px,
            preserveAspectRatio,
            position,
            pageBreakAfter: pageBreakBetweenImages && pageIndex < requestedPages.length - 1,
          });
        }
      } catch (error) {
        Logger.warn("insertImageInstructions.ts: Failed to resolve document image insertion token:", error);
        resolvedItems.push({ type: "text", content: altText });
      }
    }
  }

  return resolvedItems;
};
